import DetailNavTitle from '@/components/business/detail-nav-title';

import {goBack} from '@/utils';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import CardItem from './card-item';
import {
  getVipConfigListV2,
  getVipWeekCardDays,
  getVipWeekCardList,
  VipWeekCardConfigVO,
} from '../vip.service';
import {WeekCardContext} from './context';
import {useFocusEffect} from '@react-navigation/native';
import globalStore from '@/services/global.state';
import {ReactComponent as Question} from '@/assets/icons/vip/question.svg';
import WeekCardRules, {WeekCardRulesRef} from '../modal/week-card-rules';
import Days from './days';
import {WeeklyCardType} from '../vip.variable';
import Spin from '@/components/basic/spin';
import {getCurrentVip} from '@/utils/vip';

export const canUseType = ['silver', 'gold', 'diamond'];

const WeekCard = () => {
  const {t} = useTranslation();
  const [list, setList] = useState<VipWeekCardConfigVO[]>([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(false);
  const [level, setLevel] = useState(0);
  const [daysItem, setDaysItem] = useState<{
    silver: number;
    gold: number;
    diamond: number;
  }>({
    silver: 0,
    gold: 0,
    diamond: 0,
  });
  useEffect(() => {
    getVipWeekCardList().then(setList);
    getVipConfigListV2().then(res => {
      const {currentVip} = getCurrentVip(res);
      setLevel(currentVip?.level || 0);
    });
  }, [version]);
  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      getVipWeekCardDays()
        .then(res => {
          const filteredList = res.filter(item =>
            canUseType.some(type =>
              item.cardName?.toLowerCase().includes(type),
            ),
          );
          const map: {silver: number; gold: number; diamond: number} = {
            silver: 0,
            gold: 0,
            diamond: 0,
          };
          filteredList.forEach(item => {
            const cardType = canUseType.find(type =>
              item.cardName?.toLowerCase().includes(type),
            );
            if (cardType) {
              map[cardType as keyof typeof map] = item.validDay || 0;
            }
          });
          setDaysItem(map);
        })
        .finally(() => {
          setLoading(false);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [version]),
  );
  const filteredWeekCardList = useMemo(() => {
    return list.filter(item =>
      canUseType.some(type => item.cardName?.toLowerCase().includes(type)),
    );
  }, [list]);
  useFocusEffect(
    useCallback(() => {
      globalStore.updateAmount.next({});
    }, []),
  );
  const rulesRef = useRef<WeekCardRulesRef>(null);
  return (
    <div className="flex flex-col w-[23.4375rem] h-full fixed top-0 left-1/2 -translate-x-1/2 bg-white overflow-hidden">
      <DetailNavTitle
        title={t('vip.label.weekCard')}
        onBack={() => goBack()}
        hideServer
        hideAmount
        rightNode={
          <Question
            className="size-6"
            onClick={() => rulesRef.current?.open()}
          />
        }
      />
      <WeekCardContext.Provider
        value={{
          level,
          onRefresh: () => setVersion(v => v + 1),
        }}>
        <Spin loading={loading} style={{flex: 1}}>
          <div className="flex-1 overflow-auto">
            <div className="flex flex-col gap-5 px-3 pb-3 pt-6">
              {filteredWeekCardList.map(item => {
                const cardType = canUseType.find(type =>
                  item.cardName?.toLowerCase().includes(type),
                );
                if (!cardType) {
                  return null;
                }
                return (
                  <CardItem
                    key={item.cardId}
                    type={cardType as WeeklyCardType}
                    cardId={item.cardId}
                    prizes={{
                      spin: Number(item.spinCount),
                      scratchCard: Number(item.scratchCount),
                      money: item.bonusAmount || 0,
                      immediateGet: item.bonus || 0,
                    }}
                    price={item.price || 0}
                  />
                );
              })}
            </div>
            <Days daysItem={daysItem} />
          </div>
        </Spin>
      </WeekCardContext.Provider>

      <WeekCardRules ref={rulesRef} />
    </div>
  );
};

export default WeekCard;
